<template>
  <NavbarApp />
  <div class="cookie-policy-container">
    <h2 class="cookie-policy-title">
      {{ $t('cookiePolicyPage.title') }}
    </h2>
    <p class="cookie-policy-updated">{{ $t('cookiePolicyPage.lastUpdated') }}</p>

    <ul class="cookie-policy-list">
      <li id="section1">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section1.title') }}</h3>
        <p>{{ $t('cookiePolicyPage.section1.paragraph1') }}</p>
        <p>{{ $t('cookiePolicyPage.section1.paragraph2') }}</p>
      </li>

      <li id="section2">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section2.title') }}</h3>
        <p>{{ $t('cookiePolicyPage.section2.paragraph') }}</p>

        <div class="cookie-table-wrapper">
          <table class="cookie-table">
            <thead>
              <tr>
                <th>{{ $t('cookiePolicyPage.section2.table.category') }}</th>
                <th>{{ $t('cookiePolicyPage.section2.table.provider') }}</th>
                <th>{{ $t('cookiePolicyPage.section2.table.purpose') }}</th>
                <th>{{ $t('cookiePolicyPage.section2.table.duration') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td data-label="Category">{{ $t('cookiePolicyPage.section2.necessary.name') }}</td>
                <td data-label="Provider">IS.LAW ({{ $t('cookiePolicyPage.section2.necessary.storage') }})</td>
                <td data-label="Purpose">{{ $t('cookiePolicyPage.section2.necessary.purpose') }}</td>
                <td data-label="Duration">{{ $t('cookiePolicyPage.section2.necessary.duration') }}</td>
              </tr>
              <tr>
                <td data-label="Category">{{ $t('cookiePolicyPage.section2.maps.name') }}</td>
                <td data-label="Provider">Google Ireland Ltd. / Google LLC</td>
                <td data-label="Purpose">{{ $t('cookiePolicyPage.section2.maps.purpose') }}</td>
                <td data-label="Duration">{{ $t('cookiePolicyPage.section2.maps.duration') }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>{{ $t('cookiePolicyPage.section2.noTracking') }}</p>
      </li>

      <li id="section3">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section3.title') }}</h3>
        <p>{{ $t('cookiePolicyPage.section3.paragraph1') }}</p>
        <p>{{ $t('cookiePolicyPage.section3.paragraph2') }}</p>
        <p>
          {{ $t('cookiePolicyPage.section3.googleInfo') }}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            class="cookie-policy-external-link"
          >https://policies.google.com/privacy</a>
          {{ $t('cookiePolicyPage.section3.googleInfoAnd') }}
          <a
            href="https://policies.google.com/technologies/cookies"
            target="_blank"
            rel="noopener noreferrer"
            class="cookie-policy-external-link"
          >https://policies.google.com/technologies/cookies</a>.
        </p>
      </li>

      <li id="section4">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section4.title') }}</h3>
        <p>{{ $t('cookiePolicyPage.section4.paragraph1') }}</p>

        <div class="consent-status-box">
          <p class="consent-status-text">
            <strong>{{ $t('cookiePolicyPage.section4.currentStatus') }}</strong>
            <span :class="allowExternalMedia ? 'status-granted' : 'status-denied'">
              {{ allowExternalMedia
                ? $t('cookiePolicyPage.section4.statusGranted')
                : $t('cookiePolicyPage.section4.statusDenied') }}
            </span>
          </p>
          <button type="button" class="consent-manage-btn" @click="openPreferences">
            {{ $t('cookiePolicyPage.section4.manageButton') }}
          </button>
        </div>

        <p>{{ $t('cookiePolicyPage.section4.paragraph2') }}</p>
      </li>

      <li id="section5">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section5.title') }}</h3>
        <p>{{ $t('cookiePolicyPage.section5.paragraph') }}</p>
      </li>

      <li id="section6">
        <h3 class="cookie-policy-subtitle">{{ $t('cookiePolicyPage.section6.title') }}</h3>
        <p>
          {{ $t('cookiePolicyPage.section6.paragraph') }}
          <a href="mailto:office@islaw.ro" class="cookie-policy-external-link">office@islaw.ro</a>.
        </p>
      </li>
    </ul>
  </div>
  <Section4 />
</template>

<script>
import NavbarApp from '../components/HomePage/navbar-law.vue';
import Section4 from '../components/HomePage/section-4.vue';
import { useCookieConsent } from '../composables/useCookieConsent.js';

export default {
  name: 'CookiePolicy',
  components: {
    NavbarApp,
    Section4,
  },
  setup() {
    const { allowExternalMedia, openPreferences } = useCookieConsent();
    return { allowExternalMedia, openPreferences };
  },
};
</script>

<style scoped>
.cookie-policy-container {
  text-align: justify;
  text-justify: inter-word;
  max-width: 900px;
  margin: 40px auto;
  padding: 20px 20px 40px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.cookie-policy-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  border-bottom: 2px solid #ddd;
  padding-bottom: 1rem;
}

.cookie-policy-updated {
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 2rem;
}

.cookie-policy-subtitle {
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
  margin-top: 2rem;
  margin-bottom: 1rem;
}

p {
  margin-bottom: 1.5rem;
  color: #333;
  line-height: 1.6;
}

.cookie-policy-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cookie-policy-list > li {
  margin-bottom: 2.5rem;
}

.cookie-policy-external-link {
  color: #bbd55a;
  font-weight: 500;
  text-decoration: none;
  word-break: break-all;
  transition: color 0.3s ease;
}

.cookie-policy-external-link:hover {
  color: #a0c24a;
  text-decoration: underline;
}

/* --- Cookie table --- */
.cookie-table-wrapper {
  overflow-x: auto;
  margin-bottom: 1.5rem;
}

.cookie-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  text-align: left;
}

.cookie-table th {
  background-color: #1c1c1c;
  color: #fff;
  font-weight: 500;
  padding: 12px 15px;
  white-space: nowrap;
}

.cookie-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
  vertical-align: top;
  color: #444;
}

.cookie-table tbody tr:nth-child(even) {
  background-color: #f9f9f9;
}

/* --- Consent status / manage box --- */
.consent-status-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 1.5rem;
}

.consent-status-text {
  margin: 0;
  font-size: 1rem;
}

.status-granted {
  color: #7da33c;
  font-weight: 600;
  margin-left: 6px;
}

.status-denied {
  color: #b05555;
  font-weight: 600;
  margin-left: 6px;
}

.consent-manage-btn {
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 12px 26px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  background-color: #bbd55a;
  color: #1c1c1c;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.consent-manage-btn:hover {
  background-color: #a0c24a;
}

/* --- Responsive --- */
@media (max-width: 768px) {
  .cookie-policy-container {
    margin: 20px;
    padding: 15px;
  }

  .cookie-policy-title {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .cookie-policy-subtitle {
    font-size: 1.3rem;
  }

  .consent-status-box {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }
}
</style>
